import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import Modal from './Modal';
import InfoButton from './InfoButton';
import { operationRecordsInfo } from './operationsInfo';
import {
  getOperationRecords,
  getOperationRecordById,
  createOperationRecord,
  updateOperationRecord,
  getDeviceCatalogEntries,
  getProductsDictionary,
  getAvailableOperationsForRecord,
} from '../../services/adminService';
import {
  OperationRecord,
  OperationRecordPayload,
  OperationDevicePayload,
  OperationObjectivePayload,
  DeviceCatalogEntry,
} from '../../types/device';
import { OperationDictionary } from '../../types/operation';
import {
  AdminTable,
  AdminTableRow,
  AdminPrimaryButton,
  AdminSecondaryButton,
  AdminInlineButton,
  AdminDangerButton,
  AdminForm,
  AdminFormGroup,
  AdminFormLabel,
  AdminFormInput,
  AdminFormSelect,
  AdminCheckbox,
  AdminCheckboxLabel,
  AdminStatusBadge,
  AdminChip,
  AdminChipList,
  AdminErrorMessage,
  AdminSuccessMessage,
  AdminLoadingMessage,
  AdminFormActions,
  AdminEmptyState,
  adminColors,
} from '../../styles/adminStyles';

const Container = styled.div`
  margin-bottom: 20px;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
`;

const PageTitle = styled.h2`
  font-family: 'Rajdhani', sans-serif;
  font-size: 1.75rem;
  color: #ffffff;
  text-transform: uppercase;
  letter-spacing: 2px;
  margin: 0;
  font-weight: 700;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 120px 1fr 80px 100px 100px;
  background: linear-gradient(135deg, rgba(0, 212, 255, 0.1) 0%, rgba(0, 164, 255, 0.05) 100%);
  font-family: 'Courier New', monospace;
  font-size: 13px;
  color: #ffffff !important;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  padding: 14px 20px;
  border-bottom: 1px solid ${adminColors.borderLight};
`;

const TableRow = styled(AdminTableRow)`
  grid-template-columns: 120px 1fr 80px 100px 100px;
  color: #ffffff !important;
  font-weight: 600;
  font-size: 13px;

  & > div {
    color: #ffffff !important;
    font-size: 13px;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 12px;
`;

const Section = styled.div`
  margin-top: 24px;
  padding: 16px;
  background: rgba(0, 212, 255, 0.05);
  border: 1px solid rgba(0, 212, 255, 0.2);
  border-radius: 10px;
`;

const SectionTitle = styled.div`
  font-family: 'Courier New', monospace;
  font-size: 16px;
  color: #ffffff !important;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 16px;
  font-weight: 700;
`;

const ItemCard = styled.div`
  background: rgba(0, 30, 60, 0.4);
  border: 1px solid rgba(0, 212, 255, 0.15);
  border-radius: 8px;
  padding: 14px;
  margin-bottom: 12px;
`;

const ItemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

const ItemTitle = styled.span`
  font-family: 'Courier New', monospace;
  font-size: 14px;
  color: #ffffff !important;
  font-weight: 700;
  text-transform: uppercase;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 12px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const SmallFormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const SmallLabel = styled.label`
  font-family: 'Courier New', monospace;
  font-size: 12px;
  color: #ffffff !important;
  text-transform: uppercase;
  font-weight: 700;
`;

const SmallInput = styled.input`
  background: rgba(0, 30, 60, 0.6);
  border: 1px solid rgba(0, 212, 255, 0.2);
  border-radius: 6px;
  padding: 10px 12px;
  color: #ffffff;
  font-family: 'Courier New', monospace;
  font-size: 13px;
  font-weight: 600;

  &:focus {
    outline: none;
    border-color: rgba(0, 212, 255, 0.5);
    box-shadow: 0 0 10px rgba(0, 212, 255, 0.2);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
`;

const SmallSelect = styled.select`
  background: rgba(0, 30, 60, 0.6);
  border: 1px solid rgba(0, 212, 255, 0.2);
  border-radius: 6px;
  padding: 10px 12px;
  color: #ffffff;
  font-family: 'Courier New', monospace;
  font-size: 13px;
  font-weight: 600;

  &:focus {
    outline: none;
    border-color: rgba(0, 212, 255, 0.5);
    box-shadow: 0 0 10px rgba(0, 212, 255, 0.2);
  }

  option {
    background: rgba(0, 30, 60, 1);
  }
`;

const AddButton = styled.button`
  background: linear-gradient(135deg, rgba(0, 212, 255, 0.15) 0%, rgba(0, 164, 255, 0.08) 100%);
  border: 1px solid rgba(0, 212, 255, 0.4);
  color: #ffffff;
  padding: 10px 16px;
  font-family: 'Courier New', monospace;
  font-size: 13px;
  font-weight: 700;
  border-radius: 6px;
  cursor: pointer;
  text-transform: uppercase;

  &:hover {
    background: linear-gradient(135deg, rgba(0, 212, 255, 0.25) 0%, rgba(0, 164, 255, 0.15) 100%);
  }
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  margin-top: 20px;
  font-family: 'Courier New', monospace;
  font-size: 14px;
  color: #ffffff !important;
  font-weight: 700;
`;

const WhiteEmptyState = styled(AdminEmptyState)`
  color: #ffffff !important;
  font-weight: 700;
  font-size: 14px;
`;

const DataTable = styled.div`
  border: 1px solid rgba(0, 212, 255, 0.3);
  background: rgba(0, 30, 60, 0.3);
  margin-bottom: 20px;
  border-radius: 8px;
  overflow: hidden;
`;

interface FormState {
  uniqueCode: string;
  operationId: string;
  devices: OperationDevicePayload[];
  objectives: OperationObjectivePayload[];
}

const initialFormState: FormState = {
  uniqueCode: '',
  operationId: '',
  devices: [],
  objectives: [],
};

const OperationRecordManager: React.FC = () => {
  const [records, setRecords] = useState<OperationRecord[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [formState, setFormState] = useState<FormState>(initialFormState);
  const [editedRecord, setEditedRecord] = useState<OperationRecord | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [deviceCatalog, setDeviceCatalog] = useState<DeviceCatalogEntry[]>([]);
  const [products, setProducts] = useState<OperationDictionary[]>([]);
  const [availableOperations, setAvailableOperations] = useState<OperationDictionary[]>([]);

  const pageSize = 20;

  const loadRecords = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getOperationRecords(page, pageSize);
      setRecords(data.records || []);
      setTotalCount(data.totalCount || 0);
    } catch (err) {
      console.error('Failed to load operation records', err);
      setError(err instanceof Error ? err.message : 'Unable to load operation records');
    } finally {
      setLoading(false);
    }
  };

  const loadDeviceCatalog = async () => {
    try {
      const data = await getDeviceCatalogEntries(false);
      setDeviceCatalog(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load device catalog', err);
    }
  };

  const loadProducts = async () => {
    try {
      const data = await getProductsDictionary();
      setProducts(data);
    } catch (err) {
      console.error('Failed to load products', err);
    }
  };

  useEffect(() => {
    loadRecords();
  }, [page]);

  useEffect(() => {
    loadDeviceCatalog();
    loadProducts();
  }, []);

  const getOperationName = (operationId: string | undefined) => {
    if (!operationId) return '-';
    const product = products.find(p => p.id === operationId);
    return product ? product.title : operationId;
  };

  const handleCreateClick = async () => {
    setEditedRecord(null);
    setFormState(initialFormState);
    setError(null);
    setSuccess(null);
    // Загружаем доступные операции (которых ещё нет в operation_records)
    try {
      const available = await getAvailableOperationsForRecord();
      setAvailableOperations(available);
    } catch (err) {
      console.error('Failed to load available operations', err);
    }
    setModalOpen(true);
  };

  const handleEditClick = async (record: OperationRecord) => {
    setEditedRecord(record);
    setFormState({
      uniqueCode: record.uniqueCode,
      operationId: record.operationId,
      devices: record.devices.map(d => ({
        entryId: d.entryId,
        dcId: d.dcId,
        deviceName: d.deviceName,
        isCritical: d.isCritical,
      })),
      objectives: record.objectives.map(o => ({
        objectiveId: o.objectiveId,
        objectiveName: o.objectiveName,
        objectiveType: o.objectiveType,
        dcId: o.dcId,
        commandCode: o.commandCode,
        isCritical: o.isCritical,
        canBeModified: o.canBeModified,
        defaultValue: o.defaultValue,
        team: o.team,
        targetCount: o.targetCount,
        durationSeconds: o.durationSeconds,
        messageInterval: o.messageInterval,
        expectedMessageCount: o.expectedMessageCount,
        commandA: o.commandA,
        commandAPoints: o.commandAPoints,
        commandB: o.commandB,
        commandBPoints: o.commandBPoints,
        startingPoints: o.startingPoints,
        points: o.points,
        displayObjective: o.displayObjective,
      })),
    });
    setError(null);
    setSuccess(null);
    setModalOpen(true);
  };

  const handleAddDevice = () => {
    setFormState({
      ...formState,
      devices: [...formState.devices, {
        dcId: '',
        deviceName: '',
        isCritical: false,
      }],
    });
  };

  const handleRemoveDevice = (index: number) => {
    setFormState({
      ...formState,
      devices: formState.devices.filter((_, i) => i !== index),
    });
  };

  const handleDeviceChange = (index: number, field: keyof OperationDevicePayload, value: any) => {
    const updated = [...formState.devices];
    updated[index] = { ...updated[index], [field]: value };
    setFormState({ ...formState, devices: updated });
  };

  const handleAddObjective = () => {
    setFormState({
      ...formState,
      objectives: [...formState.objectives, {
        objectiveName: '',
        objectiveType: 'count',
        isCritical: false,
        canBeModified: false,
        team: 'human',
        points: 0,
        displayObjective: false,
      }],
    });
  };

  const handleRemoveObjective = (index: number) => {
    setFormState({
      ...formState,
      objectives: formState.objectives.filter((_, i) => i !== index),
    });
  };

  const handleObjectiveChange = (index: number, field: keyof OperationObjectivePayload, value: any) => {
    const updated = [...formState.objectives];
    updated[index] = { ...updated[index], [field]: value };
    setFormState({ ...formState, objectives: updated });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!formState.uniqueCode.trim() || !formState.operationId) {
      setError('Unique code and product are required');
      return;
    }

    if (formState.devices.length === 0) {
      setError('At least one device is required');
      return;
    }

    const payload: OperationRecordPayload = {
      uniqueCode: formState.uniqueCode.trim(),
      operationId: formState.operationId.trim(),
      devices: formState.devices,
      objectives: formState.objectives,
    };

    setSaving(true);
    try {
      if (editedRecord) {
        await updateOperationRecord(editedRecord.id, payload);
        setSuccess('Operation record updated');
      } else {
        await createOperationRecord(payload);
        setSuccess('Operation record created');
      }
      setModalOpen(false);
      setFormState(initialFormState);
      setEditedRecord(null);
      await loadRecords();
    } catch (err) {
      console.error('Failed to save operation record', err);
      setError(err instanceof Error ? err.message : 'Unable to save operation record');
    } finally {
      setSaving(false);
    }
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <Container>
      <PageHeader>
        <PageTitle>Operation Records</PageTitle>
        <InfoButton title="Operation Records Help" sections={operationRecordsInfo} />
      </PageHeader>
      <HeaderActions>
        <div></div>
        <AdminPrimaryButton onClick={handleCreateClick}>+ Add Operation</AdminPrimaryButton>
      </HeaderActions>

      {loading && <AdminLoadingMessage>Loading...</AdminLoadingMessage>}
      {error && !modalOpen && <AdminErrorMessage>{error}</AdminErrorMessage>}

      <DataTable>
        <TableHeader>
          <div>Code</div>
          <div>Product</div>
          <div>Devices</div>
          <div>Created</div>
          <div>Actions</div>
        </TableHeader>
        {records.map((record) => (
          <TableRow key={record.id}>
            <div style={{ fontFamily: 'Courier New', fontSize: '13px', color: '#ffffff', fontWeight: 700 }}>{record.uniqueCode}</div>
            <div style={{ color: '#ffffff', fontWeight: 700, fontSize: '13px' }}>{getOperationName(record.operationId)}</div>
            <div>
              <AdminChip>{record.devices.length}</AdminChip>
            </div>
            <div style={{ fontSize: '13px', color: '#ffffff', fontWeight: 700 }}>
              {new Date(record.createdAt).toLocaleDateString()}
            </div>
            <div>
              <AdminInlineButton onClick={() => handleEditClick(record)}>Edit</AdminInlineButton>
            </div>
          </TableRow>
        ))}
        {records.length === 0 && !loading && (
          <WhiteEmptyState>No operation records found</WhiteEmptyState>
        )}
      </DataTable>

      {totalPages > 1 && (
        <Pagination>
          <AdminSecondaryButton disabled={page === 1} onClick={() => setPage(p => p - 1)}>
            Prev
          </AdminSecondaryButton>
          <span>Page {page} of {totalPages}</span>
          <AdminSecondaryButton disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
            Next
          </AdminSecondaryButton>
        </Pagination>
      )}

      <Modal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditedRecord(null);
          setFormState(initialFormState);
        }}
        title={editedRecord ? 'Edit Operation Record' : 'Create Operation Record'}
      >
        <AdminForm onSubmit={handleSubmit}>
          <FormRow>
            <AdminFormGroup>
              <AdminFormLabel>Unique Code (10 digits)</AdminFormLabel>
              <AdminFormInput
                value={formState.uniqueCode}
                onChange={(e) => setFormState({ ...formState, uniqueCode: e.target.value })}
                placeholder="1234567890"
                maxLength={10}
              />
            </AdminFormGroup>
            <AdminFormGroup>
              <AdminFormLabel>Operation *</AdminFormLabel>
              <AdminFormSelect
                value={formState.operationId}
                onChange={(e) => {
                  setFormState({
                    ...formState,
                    operationId: e.target.value
                  });
                }}
                disabled={!!editedRecord}
              >
                <option value="">Select operation...</option>
                {editedRecord ? (
                  // При редактировании показываем все операции
                  products.map(p => (
                    <option key={p.id} value={p.id}>{p.title}</option>
                  ))
                ) : (
                  // При создании показываем только доступные
                  availableOperations.map(p => (
                    <option key={p.id} value={p.id}>{p.title}</option>
                  ))
                )}
              </AdminFormSelect>
            </AdminFormGroup>
          </FormRow>

          <Section>
            <SectionTitle>Devices ({formState.devices.length})</SectionTitle>
            {formState.devices.map((device, index) => (
              <ItemCard key={index}>
                <ItemHeader>
                  <ItemTitle>Device #{index + 1}</ItemTitle>
                  <AdminDangerButton type="button" onClick={() => handleRemoveDevice(index)}>
                    Remove
                  </AdminDangerButton>
                </ItemHeader>
                <FormRow>
                  <SmallFormGroup>
                    <SmallLabel>Device Catalog</SmallLabel>
                    <SmallSelect
                      value={device.dcId}
                      onChange={(e) => handleDeviceChange(index, 'dcId', e.target.value)}
                    >
                      <option value="">Select device...</option>
                      {deviceCatalog.map(d => (
                        <option key={d.id} value={d.id}>{d.deviceType} - {d.model}</option>
                      ))}
                    </SmallSelect>
                  </SmallFormGroup>
                  <SmallFormGroup>
                    <SmallLabel>Device Name</SmallLabel>
                    <SmallInput
                      value={device.deviceName}
                      onChange={(e) => handleDeviceChange(index, 'deviceName', e.target.value)}
                      placeholder="Bomb A"
                    />
                  </SmallFormGroup>
                </FormRow>
                <AdminCheckboxLabel>
                  <AdminCheckbox
                    type="checkbox"
                    checked={device.isCritical}
                    onChange={(e) => handleDeviceChange(index, 'isCritical', e.target.checked)}
                  />
                  Critical device
                </AdminCheckboxLabel>
              </ItemCard>
            ))}
            <AddButton type="button" onClick={handleAddDevice}>+ Add Device</AddButton>
          </Section>

          <Section>
            <SectionTitle>Objectives ({formState.objectives.length})</SectionTitle>
            {formState.objectives.map((objective, index) => (
              <ItemCard key={index}>
                <ItemHeader>
                  <ItemTitle>Objective #{index + 1}</ItemTitle>
                  <AdminDangerButton type="button" onClick={() => handleRemoveObjective(index)}>
                    Remove
                  </AdminDangerButton>
                </ItemHeader>
                <FormRow>
                  <SmallFormGroup>
                    <SmallLabel>Name</SmallLabel>
                    <SmallInput
                      value={objective.objectiveName}
                      onChange={(e) => handleObjectiveChange(index, 'objectiveName', e.target.value)}
                      placeholder="Arm the bomb"
                    />
                  </SmallFormGroup>
                  <SmallFormGroup>
                    <SmallLabel>Type</SmallLabel>
                    <SmallSelect
                      value={objective.objectiveType}
                      onChange={(e) => handleObjectiveChange(index, 'objectiveType', e.target.value)}
                    >
                      <option value="count">Count</option>
                      <option value="time">Time</option>
                      <option value="points">Points</option>
                    </SmallSelect>
                  </SmallFormGroup>
                </FormRow>
                <FormRow>
                  <SmallFormGroup>
                    <SmallLabel>Team</SmallLabel>
                    <SmallSelect
                      value={objective.team}
                      onChange={(e) => handleObjectiveChange(index, 'team', e.target.value)}
                    >
                      <option value="human">Human</option>
                      <option value="alien">Alien</option>
                    </SmallSelect>
                  </SmallFormGroup>
                  <SmallFormGroup>
                    <SmallLabel>Related Device</SmallLabel>
                    <SmallSelect
                      value={objective.dcId || ''}
                      onChange={(e) => handleObjectiveChange(index, 'dcId', e.target.value || undefined)}
                    >
                      <option value="">None</option>
                      {deviceCatalog.map(d => (
                        <option key={d.id} value={d.id}>
                          {d.deviceType} - {d.model}
                        </option>
                      ))}
                    </SmallSelect>
                  </SmallFormGroup>
                  <SmallFormGroup>
                    <SmallLabel>Command Code</SmallLabel>
                    <SmallSelect
                      value={objective.commandCode || ''}
                      onChange={(e) => handleObjectiveChange(index, 'commandCode', e.target.value || undefined)}
                    >
                      <option value="">None</option>
                      {objective.dcId && deviceCatalog.find(d => d.id === objective.dcId)?.commands.map(cmd => (
                        <option key={cmd.code} value={cmd.code}>
                          {cmd.code} - {cmd.name}
                        </option>
                      ))}
                    </SmallSelect>
                  </SmallFormGroup>
                </FormRow>
                {objective.objectiveType === 'count' && (
                  <SmallFormGroup>
                    <SmallLabel>Target Count</SmallLabel>
                    <SmallInput
                      type="number"
                      value={objective.targetCount || ''}
                      onChange={(e) => handleObjectiveChange(index, 'targetCount', e.target.value ? parseInt(e.target.value) : undefined)}
                      placeholder="5"
                    />
                  </SmallFormGroup>
                )}
                {objective.objectiveType === 'time' && (
                  <SmallFormGroup>
                    <SmallLabel>Duration (seconds)</SmallLabel>
                    <SmallInput
                      type="number"
                      value={objective.durationSeconds || ''}
                      onChange={(e) => handleObjectiveChange(index, 'durationSeconds', e.target.value ? parseInt(e.target.value) : undefined)}
                      placeholder="300"
                    />
                  </SmallFormGroup>
                )}
                {objective.objectiveType === 'points' && (
                  <FormRow>
                    <SmallFormGroup>
                      <SmallLabel>Command A</SmallLabel>
                      <SmallInput
                        value={objective.commandA || ''}
                        onChange={(e) => handleObjectiveChange(index, 'commandA', e.target.value)}
                        placeholder="BOMB_ARM"
                      />
                    </SmallFormGroup>
                    <SmallFormGroup>
                      <SmallLabel>Command A Points</SmallLabel>
                      <SmallInput
                        type="number"
                        value={objective.commandAPoints || ''}
                        onChange={(e) => handleObjectiveChange(index, 'commandAPoints', e.target.value ? parseInt(e.target.value) : undefined)}
                        placeholder="10"
                      />
                    </SmallFormGroup>
                  </FormRow>
                )}
                <SmallFormGroup>
                  <SmallLabel>Points (bonus for completion)</SmallLabel>
                  <SmallInput
                    type="number"
                    value={objective.points || 0}
                    onChange={(e) => handleObjectiveChange(index, 'points', e.target.value ? parseInt(e.target.value) : 0)}
                    placeholder="0"
                  />
                </SmallFormGroup>
                <FormRow>
                  <AdminCheckboxLabel>
                    <AdminCheckbox
                      type="checkbox"
                      checked={objective.isCritical}
                      onChange={(e) => handleObjectiveChange(index, 'isCritical', e.target.checked)}
                    />
                    Critical
                  </AdminCheckboxLabel>
                  <AdminCheckboxLabel>
                    <AdminCheckbox
                      type="checkbox"
                      checked={objective.canBeModified}
                      onChange={(e) => handleObjectiveChange(index, 'canBeModified', e.target.checked)}
                    />
                    Can be modified
                  </AdminCheckboxLabel>
                  <AdminCheckboxLabel>
                    <AdminCheckbox
                      type="checkbox"
                      checked={objective.displayObjective}
                      onChange={(e) => handleObjectiveChange(index, 'displayObjective', e.target.checked)}
                    />
                    Display on mission page
                  </AdminCheckboxLabel>
                </FormRow>
              </ItemCard>
            ))}
            <AddButton type="button" onClick={handleAddObjective}>+ Add Objective</AddButton>
          </Section>

          {error && <AdminErrorMessage>{error}</AdminErrorMessage>}
          {success && <AdminSuccessMessage>{success}</AdminSuccessMessage>}

          <AdminFormActions>
            <AdminSecondaryButton type="button" onClick={() => setModalOpen(false)}>
              Cancel
            </AdminSecondaryButton>
            <AdminPrimaryButton type="submit" disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </AdminPrimaryButton>
          </AdminFormActions>
        </AdminForm>
      </Modal>
    </Container>
  );
};

export default OperationRecordManager;
